import { Location } from '@angular/common';
import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { IManageAudienceUsersActionPayload } from 'app/library/audience-users/interfaces/audience-users-action.action-payload.interface';
import { IManageExperienceUsersActionPayload } from 'app/library/experience-user/interfaces/experience-users-action.action-payload.interface';
import { ExperienceUserExtended } from 'app/library/experience-user/models/experience-user.extended.model';
import { ExperienceUser } from 'app/library/experience-user/models/experience-user.model';
import { DeselectSelectedExperienceUsers, SearchExperienceUsers, SelectFilteredExperienceUsers, ToggleSelectedExperienceUser } from 'app/library/experience-users/actions';
import { ExperienceUsersState } from 'app/library/experience-users/models/experience-users.state';
import { ExperienceUsersService } from 'app/library/experience-users/services/experience-users.service';
import { MaxBrainExperience } from 'app/library/experience/models/experience.model';
import { ExperienceService } from 'app/library/experience/services/experience.service';
import { SelectGroupMembersDialogComponent } from 'app/library/group/components/dialogs/select-group-members-dialog/select-group-members-dialog.component';
import { EngageMoveExperienceMembersDialogComponent } from 'app/library/grow/dialogs/engage-move-experience-members-dialog/engage-move-experience-members-dialog.component';
import { SidenavService } from 'app/library/grow/services/sidenav.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { AutocompleteDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/autocomplete/autocomplete.component';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { ILanguageService } from 'app/projects/shared/src/lib/interfaces/language.interface';
import { MAXBRAIN_LANGUAGE_SERVICE } from 'app/projects/shared/src/lib/services/language.token';
import { CreateUserSuccess } from 'app/projects/user/src/lib/actions/create-entity-success.action';
import { DeselectSelectedUsers } from 'app/projects/user/src/lib/actions/deselect-selected-entities.action';
import { UpdateUserStatusBulkSuccess } from 'app/projects/user/src/lib/actions/update-user-status-bulk-success.action';
import { UpdateUserStatusBulk } from 'app/projects/user/src/lib/actions/update-user-status-bulk.action';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserStatuses } from 'app/projects/user/src/lib/models/user-statuses';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-experience-users',
    templateUrl: './experience-users.component.html',
    styleUrls: ['./experience-users.component.scss'],
})
export class ExperienceUsersComponent extends UnsubscribeOnDestroy implements OnInit {
    private _isUnassignedSelected = new BehaviorSubject<boolean>(sessionStorage.getItem('isUnassignedSelected') === 'true');
    isUnassignedSelected$ = this._isUnassignedSelected.asObservable();

    private _loadUsers = new BehaviorSubject<boolean>(null);
    loadUser$ = this._loadUsers.asObservable();

    experienceId: string;
    experience: MaxBrainExperience;

    @Select(ExperienceUsersState.getEntities)
    users$: Observable<ExperienceUser[]>;

    @Select(ExperienceUsersState.getEntitiesWithUser)
    filteredExperienceUsers$: Observable<ExperienceUserExtended[]>;

    @Select(AllUsersState.getEntities)
    filteredAllUsers$: Observable<User[]>;

    @Select(ExperienceUsersState.getSelectedEntityIds)
    selectedUserIds$: Observable<string[]>;
    selectedUserIds: [{ id: string }];

    @Select(ExperienceUsersState.getTotalCount)
    totalCount$: Observable<number>;

    @Select(ExperienceUsersState.getSearchTerm)
    searchTerm$: Observable<string>;

    searchInput = new FormControl(this._store.selectSnapshot(ExperienceUsersState.getSearchTerm));

    @Select(ExperienceUsersState.isFetchingList)
    isFetchingExperienceUsers$: Observable<boolean>;

    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    dropdownDialogRef: MatDialogRef<AutocompleteDialogComponent>;
    engageMoveUsersDialogRef: MatDialogRef<EngageMoveExperienceMembersDialogComponent>;

    sortBy = this._store.selectSnapshot(ExperienceUsersState.getSortBy);
    sortOrder = this._store.selectSnapshot(ExperienceUsersState.getSortOrder);

    usersUploadInProgress = false;

    screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    @HostListener('window:resize')
    onResize() {
        this.screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    }

    constructor(
        private _experienceUsersService: ExperienceUsersService,
        private _experienceService: ExperienceService,
        private _route: ActivatedRoute,
        private _store: Store,
        private _action$: Actions,
        private _matDialog: MatDialog,
        private _location: Location,
        private _translateService: TranslateService,
        public sidenavService: SidenavService,
        @Inject(MAXBRAIN_LANGUAGE_SERVICE) public languageService: ILanguageService
    ) {
        super();
    }

    /**
     * On init
     */
    async ngOnInit(): Promise<void> {
        this.experienceId = this._route.parent.snapshot.params['id'];

        this._experienceService.experience$
            .pipe(
                filter((x) => !!x),
                take(1)
            )
            .subscribe(() => {
                this.experience = this._experienceService.getExperienceById(this.experienceId);
            });

        this._action$.pipe(takeUntil(this._unsubscribeAll), ofActionDispatched(CreateUserSuccess)).subscribe(() => {
            this._loadUsers.next(false);
        });

        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(300), distinctUntilChanged()).subscribe((searchTerm) => {
            this._store.dispatch(new SearchExperienceUsers(searchTerm));
        });

        this.selectedUserIds$
            .pipe(
                filter((selectedUserIds) => !!selectedUserIds),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((selectedUserIds) => {
                this.selectedUserIds = selectedUserIds.map((id) => ({ id: id })) as [{ id: string }];
            });
    }

    deselect(): void {
        this._store.dispatch(new DeselectSelectedUsers());
    }

    goBack() {
        this._location.back();
    }


    updateStatusBulk(): void {
        const userIds: string[] = this._store.selectSnapshot(ExperienceUsersState.getSelectedEntityIds);

        this.dropdownDialogRef = this._matDialog.open(AutocompleteDialogComponent, {
            disableClose: false,
        });

        this.dropdownDialogRef.componentInstance.dialogTitle = 'USER.MESSAGE.UPDATE_STATUS_FOR_SELECTED_USERS';
        this.dropdownDialogRef.componentInstance.placeholder = 'GENERAL.PROPERTY.STATUS';
        this.dropdownDialogRef.componentInstance.items = UserStatuses.all.map((status) => status.name);

        this.dropdownDialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this._store.dispatch(
                    new UpdateUserStatusBulk({
                        entityIds: userIds,
                        status: result.data,
                    })
                );
                this._action$.pipe(takeUntil(this._unsubscribeAll), ofActionDispatched(UpdateUserStatusBulkSuccess), take(1)).subscribe((action) => {
                    this.deselect();
                });
            }

            this.dropdownDialogRef = null;
        });
    }

    /**
     * On selected change
     */
    selectUser(experienceUserExtended: ExperienceUserExtended): void {
        this._store.dispatch(new ToggleSelectedExperienceUser(experienceUserExtended.id));
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle(isAllSelected: boolean): void {
        this._store.dispatch(isAllSelected ? new DeselectSelectedExperienceUsers() : new SelectFilteredExperienceUsers());
    }

    openDialogAddUsers(): void {
        const dialogRef = this._matDialog.open(SelectGroupMembersDialogComponent, {
            width: '680px',
            id: 'select-users-dialog',
        });

        dialogRef.componentInstance.entitie$ = this.filteredAllUsers$;
        dialogRef.componentInstance.entityId = this.experienceId;
        dialogRef.componentInstance.title = 'FORM.ASSIGN_TO_MODULES.TITLE';
        dialogRef.componentInstance.dataType = 'users';
        dialogRef.componentInstance.role = 'experienceMember';

        dialogRef.afterClosed().subscribe((result: { list: User[] }) => {
            if (result && result.list) {
                const payload: IManageExperienceUsersActionPayload = {
                    experience: { id: this.experienceId },
                    users: result.list.map((user) => ({ id: user.id })) as [{ id: string }],
                };
                this._experienceUsersService.addUsersToExperience(payload);
            }
        });
    }

    deleteExperienceUsers(): void {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
            width: '600px',
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'ENGAGE.EXPERIENCE.DIALOGS.REMOVE_USER.TITLE.PLURAL';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('ENGAGE.EXPERIENCE.DIALOGS.REMOVE_USER.MESSAGE.PLURAL', {
            experienceName: this.experience.name,
        });
        this.confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        this.confirmDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                await this._experienceUsersService.deleteExperienceUsers({ experienceUsers: this.selectedUserIds });
                this.confirmDialogRef = null;
            }
        });
    }

    openDialogMoveUsers(): void {
        this.engageMoveUsersDialogRef = this._matDialog.open(EngageMoveExperienceMembersDialogComponent, {
            disableClose: false,
            width: '600px',
        });
        const experiences = this._experienceService.getExperiences();
        this.engageMoveUsersDialogRef.componentInstance.title = 'ENGAGE.EXPERIENCE.DIALOGS.MOVE_USER.PLURAL';
        this.engageMoveUsersDialogRef.componentInstance.label = 'ENGAGE.EXPERIENCE.DIALOGS.MOVE_USER.LABEL';
        this.engageMoveUsersDialogRef.componentInstance.entityId = this.experienceId;
        this.engageMoveUsersDialogRef.componentInstance.experiences = experiences;
        this.engageMoveUsersDialogRef.componentInstance.selectedUserId$ = this.selectedUserIds$;

        this.engageMoveUsersDialogRef.afterClosed().subscribe((result: IManageAudienceUsersActionPayload) => {
            if (result) {
                this._experienceUsersService.moveExperienceUsers(result.target, result.selectedUserIds);
            }
        });
    }
}
